import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Package, CheckCircle, ClockCountdown, Warning, ArrowRight, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const FALLBACK_IMG = "https://images.pexels.com/photos/8633662/pexels-photo-8633662.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Dashboard() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });
  const { data: stale } = useQuery({
    queryKey: ["stale-products"],
    queryFn: async () => (await api.get("/notifications/stale?days=3")).data,
  });

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/notifications/digest", { days: 3 });
      toast.success(data.message + (data.ai_generated ? " (AI-written)" : ""));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const cards = [
    { label: "Total Products", value: stats?.total ?? "—", icon: Package, color: "text-slate-700 bg-slate-100" },
    { label: "Published", value: stats?.published ?? "—", icon: CheckCircle, color: "text-[#047857] bg-[#ECFDF5]" },
    { label: "In Pipeline", value: stats?.in_progress ?? "—", icon: ClockCountdown, color: "text-[#B45309] bg-[#FFFBEB]" },
    { label: "Stale (3+ days)", value: stats?.stale ?? "—", icon: Warning, color: "text-[#B91C1C] bg-red-50" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter text-slate-900">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Pipeline overview across all bakery products</p>
        </div>
        <Button onClick={() => navigate("/products")} data-testid="goto-products-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
          View Products <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="stats-grid">
        {cards.map((c) => (
          <div key={c.label} className="rounded-md border border-slate-200 bg-white p-5 transition-shadow duration-200 hover:shadow-sm" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-md ${c.color}`}>
              <c.icon size={20} weight="duotone" />
            </div>
            <div className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{c.value}</div>
            <div className="mt-0.5 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-md border border-slate-200 bg-white" data-testid="recent-products-card">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-800">Recent Activity</h2>
            <Link to="/products" className="text-xs font-semibold text-[#EA580C] hover:text-[#C2410C]">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(stats?.recent || []).length === 0 && (
              <p className="px-5 py-8 text-sm text-slate-400">No products yet. Create your first product to start the pipeline.</p>
            )}
            {(stats?.recent || []).map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} data-testid={`recent-product-${p.id}`} className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-slate-50">
                <img src={p.images?.[0] || FALLBACK_IMG} alt="" className="h-10 w-10 rounded-md border border-slate-200 object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{p.name}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <ProgressBar value={p.progress} />
                    <span className="shrink-0 text-xs text-slate-500">{p.completed_steps}/{p.total_steps}</span>
                  </div>
                </div>
                <StatusBadge status={p.status} />
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-white" data-testid="stale-widget">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="font-heading text-lg font-bold tracking-tight text-slate-800">Stale Products</h2>
            {can("notifications.manage") && (
              <Button size="sm" variant="outline" onClick={sendDigest} data-testid="send-digest-button" className="border-[#EA580C] text-[#EA580C] hover:bg-[#FFF7ED]">
                <PaperPlaneTilt size={14} className="mr-1" /> Send AI digest
              </Button>
            )}
          </div>
          <div className="divide-y divide-slate-100">
            {(stale || []).length === 0 && <p className="px-5 py-8 text-sm text-slate-400">Nothing stale. All products are moving.</p>}
            {(stale || []).slice(0, 6).map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} data-testid={`stale-product-${p.id}`} className="flex items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.pending_steps?.length} steps pending</div>
                </div>
                <span className="shrink-0 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-[#B91C1C]">
                  {p.days_stale}d idle
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
